/**
 * useActiveTripsCount · v2.7.7
 * ────────────────────────────
 * Live count of in-progress rides across the fleet. Designed for the
 * "Live Map" sidebar badge.
 *
 * Data flow
 * ─────────
 *  - Initial baseline: GET /api/admin/dispatch (returns active_rides.count)
 *  - Increment instantly on `rtg:trip_initiated` window events
 *  - Decrement on `rtg:trip_completed` events
 *  - Refresh from server every 30s (drift correction)
 *
 * Designed as a module-singleton so any number of components can subscribe
 * without multiplying API calls.
 */
import { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";

const REFRESH_MS = 30_000;
const _state = {
  count: 0,
  at: 0,
  subscribers: new Set(),
  fetching: false,
  timer: null,
};

function _notify() {
  _state.subscribers.forEach((cb) => cb(_state.count));
}

async function _fetchOnce() {
  if (_state.fetching) return;
  _state.fetching = true;
  try {
    const { data } = await adminApi.get("/admin/dispatch");
    _state.count = data?.active_rides?.count ?? 0;
    _state.at = Date.now();
    _notify();
  } catch { /* silent — keep prior count */ }
  finally { _state.fetching = false; }
}

function _ensureTimer() {
  if (_state.timer) return;
  _state.timer = setInterval(_fetchOnce, REFRESH_MS);
  if (typeof window !== "undefined") {
    window.addEventListener("rtg:trip_initiated", () => {
      _state.count += 1;
      _notify();
    });
    window.addEventListener("rtg:trip_completed", () => {
      _state.count = Math.max(0, _state.count - 1);
      _notify();
    });
    // Cancelled trips also drop the count. We listen broadly.
    window.addEventListener("rtg:trip_canceled", () => {
      _state.count = Math.max(0, _state.count - 1);
      _notify();
    });
  }
}


export default function useActiveTripsCount() {
  const [count, setCount] = useState(_state.count);
  useEffect(() => {
    _state.subscribers.add(setCount);
    if (Date.now() - _state.at > REFRESH_MS) _fetchOnce();
    _ensureTimer();
    return () => { _state.subscribers.delete(setCount); };
  }, []);
  return count;
}
